import { useEffect, useRef, useState } from 'react';
import packageJson from '../../package.json';
import Toast from './Toast/Toast';
import { getAppVersion } from './apiCalls';

const FRONTEND_VERSION = process.env.REACT_APP_VERSION || packageJson.version || '0.0.0.0';
const TEN_MINUTES = 10 * 60 * 1000;

/**
 * Polls backend app version and shows a non-blocking toast when a mismatch is detected.
 * Users can dismiss; will reappear on next poll if mismatch persists.
 */
const VersionChecker = () => {
  const [show, setShow] = useState(false);
  const [backendVersion, setBackendVersion] = useState(null);
  const timerRef = useRef(null);
  const autoHideRef = useRef(null);

  const checkVersion = async () => {
    try {
      const result = await getAppVersion();
      let apiVersion = null;
      if (Array.isArray(result)) {
        const current = result.find((r) => r?.isCurrent) || result[0];
        apiVersion = current?.version ?? current?.versionNumber ?? null;
      } else {
        apiVersion = result?.version ?? result?.versionNumber ?? null;
      }
      setBackendVersion(apiVersion);
      if (apiVersion && apiVersion !== FRONTEND_VERSION) {
        setShow(true);
      }
    } catch (err) {
      // Fail silently; do not block app if version endpoint fails.
      console.warn('Version check failed', err);
    }
  };

  useEffect(() => {
    checkVersion();
    timerRef.current = setInterval(() => {
      checkVersion();
    }, TEN_MINUTES);
    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
      if (autoHideRef.current) clearTimeout(autoHideRef.current);
    };
  }, []);

  const handleClose = () => {
    if (autoHideRef.current) clearTimeout(autoHideRef.current);
    setShow(false);
  };

  // auto-hide after 8s
  useEffect(() => {
    if (show) {
      if (autoHideRef.current) clearTimeout(autoHideRef.current);
      autoHideRef.current = setTimeout(() => setShow(false), 8000);
    }
    return () => {
      if (autoHideRef.current) clearTimeout(autoHideRef.current);
    };
  }, [show]);

  if (!show || !backendVersion) return null;

  return (
    <Toast
      className="version-toast shadow"
      style={{
        position: 'fixed',
        bottom: '1.5rem',
        right: '1.5rem',
        zIndex: 1060,
        maxWidth: '20rem',
        backgroundColor: '#0d6efd',
        color: '#fff',
        borderRadius: '0.75rem',
        padding: '1rem 1.25rem',
      }}
    >
      <div className="d-flex flex-column gap-2">
        <div className="fw-bold">New version available</div>
        <div style={{ fontSize: '0.95rem' }}>
          Please press <strong>CTRL + F5</strong> to refresh and load the latest version.
          <div className="mt-1" style={{ fontSize: '0.85rem', opacity: 0.9 }}>
            Current: {FRONTEND_VERSION} • Latest: {backendVersion}
          </div>
        </div>
        <div className="d-flex justify-content-end">
          <button className="btn btn-light btn-sm" onClick={handleClose}>
            Dismiss
          </button>
        </div>
      </div>
    </Toast>
  );
};

export default VersionChecker;
